import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Mail, MessageCircle, Clock, Send } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';
import { BACKEND_URL as CONFIG_BACKEND_URL } from '../config/api';

const BACKEND_URL = CONFIG_BACKEND_URL;

const ContactPage = () => {
  useCanonical('https://ecuflashservice.com/contact');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    orderNumber: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        orderNumber: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Contact ECU Tuning Support | Get Help | ECU Flash Service</title>
        <meta name="description" content="Contact our ECU tuning experts. Get help with DPF delete, EGR removal, AdBlue issues, DTC codes. Fast response. Email support@ecuflashservice.com" />
        <meta name="keywords" content="ECU tuning support, contact ECU service, DPF delete help, EGR delete support, ECU tuning questions" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-1 text-[#888] hover:text-[#00F0FF] transition text-sm font-semibold uppercase tracking-wider">
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-white/10 mb-6">
            <Mail className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#888]">Get in Touch</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Contact</span>{' '}
            <span className="text-[#00F0FF]">Us</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto">
            Have questions? We're here to help you with your ECU tuning needs
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-6xl">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Contact Cards */}
            <div className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#00F0FF]/30 transition-all">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Email Support</h3>
                  <p className="text-[#888] text-sm">support@ecuflashservice.com</p>
                </div>
              </div>
              <p className="text-[#666] text-sm">We respond to all emails within 24 hours during business days.</p>
            </div>

            <div className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#00F0FF]/30 transition-all">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">WhatsApp Business</h3>
                  <a href="https://wa.me/6797997609" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 font-medium">
                    +679 7997609
                  </a>
                </div>
              </div>
              <p className="text-[#666] text-sm mb-3">Quick support for urgent orders. Available during business hours.</p>
              <a 
                href="https://wa.me/6797997609" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 px-4 py-2 text-sm font-medium transition"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </div>

            <div className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#00F0FF]/30 transition-all">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Business Hours</h3>
                  <p className="text-[#888] text-sm">Mon - Sun</p>
                </div>
              </div>
              <p className="text-[#666] text-sm">10:00 AM - 10:00 PM (GMT+12)<br/>7 days a week</p>
            </div>

            <div className="relative overflow-hidden bg-gradient-to-br from-[#00F0FF]/10 to-transparent border border-[#00F0FF]/20 p-6">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#00F0FF]/5 rounded-full blur-3xl" />
              <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-white mb-2">Average Response Time</h3>
              <p className="text-4xl font-bold text-[#00F0FF] mb-2">20-60 min</p>
              <p className="text-[#666] text-sm">For file modifications during business hours</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-[#0A0A0A] border border-white/5 p-8">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">Send us a Message</h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 bg-green-500/10 border border-green-500/30 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-green-400">Message Sent Successfully!</p>
                      <p className="text-green-400/70 text-sm">We will get back to you as soon as possible.</p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 bg-[#FF003C]/10 border border-[#FF003C]/30 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#FF003C]/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#FF003C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-[#FF003C]">Failed to Send Message</p>
                      <p className="text-[#FF003C]/70 text-sm">Please try again or contact us directly via email.</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#888] uppercase tracking-wider mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#888] uppercase tracking-wider mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none transition"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#888] uppercase tracking-wider mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 234 567 8900"
                      className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#888] uppercase tracking-wider mb-2">Order Number</label>
                    <input
                      type="text"
                      name="orderNumber"
                      value={formData.orderNumber}
                      onChange={handleChange}
                      placeholder="e.g., ORD-123456"
                      className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#888] uppercase tracking-wider mb-2">Subject *</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none transition"
                  >
                    <option value="">Select a subject...</option>
                    <option value="general">General Inquiry</option>
                    <option value="quote">Request a Quote</option>
                    <option value="order-status">Order Status</option>
                    <option value="technical">Technical Support</option>
                    <option value="dtc-issue">DTC / Error Code Issue</option>
                    <option value="file-problem">Problem with Modified File</option>
                    <option value="refund">Refund Request</option>
                    <option value="partnership">Business / Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#888] uppercase tracking-wider mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Please describe your inquiry in detail. If you are reporting an issue with a modified file, please include your vehicle information and any error codes you are seeing."
                    className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none transition resize-none"
                  />
                </div>

                <div className="bg-[#1A1A1A] border border-white/10 p-4 text-sm text-[#888]">
                  <p className="flex items-start">
                    <Zap className="w-4 h-4 text-[#00F0FF] mr-2 mt-0.5 flex-shrink-0" />
                    <span>For faster support with existing orders, please include your order number and vehicle details in your message.</span>
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  <span className="flex items-center gap-2">
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
